/**
 * Une bulle posée contre un élément — ou contre un point du canvas (une ancre virtuelle) :
 * une seule mécanique pour toutes les bulles de l'app, trois intentions.
 *
 * - `tooltip` : nomme un contrôle (et son raccourci). Au survol ou au focus, après un court
 *   délai ; part quand on sort, ou glisse vers la cible voisine. Voir attachTooltip.
 * - `callout` : signale un état (« image à fournir »). Reste tant que l'état existe — c'est
 *   l'appelant qui la retire.
 * - `coach` : explique un geste la première fois qu'il devient possible, une seule fois
 *   (voir coach_marks.ts). Elle se ferme d'un clic.
 *
 * La bulle vit dans le <body>, en position fixed : dans le contenu, elle serait rognée par
 * un conteneur qui défile ou décalée par un ancêtre transformé. Elle suit son ancre
 * (autoUpdate) et se cache quand l'ancre sort de la vue. Le style est ici, en utilitaires ;
 * le JS ne pose que des attributs (`data-kind`, `data-side`, `data-state`, `data-mode`,
 * `role`) et des valeurs calculées en style.
 *
 * Toutes les bulles sont à l'inverse de leur surface (editor-round-2 § 15) : claires sur
 * l'éditeur sombre, sombres sur l'app claire — les tokens de la marque, dans l'autre mode.
 * Elles entrent depuis leur cible et repartent vers elle (keyframes bubble-in / bubble-out,
 * grille de mouvement), en grandissant depuis leur flèche.
 */
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
  type Placement,
  type VirtualElement,
} from "@floating-ui/dom";

export type BubbleKind = "tooltip" | "callout" | "coach";
export type BubbleAnchor = Element | VirtualElement;

export interface BubbleOptions {
  kind: BubbleKind
  /** Le texte (les retours à la ligne sont gardés). */
  text?: string
  /** Un contenu riche à la place du texte (cloné tel quel). */
  content?: Node
  placement?: Placement
  /** Coach : le libellé du bouton qui ferme la bulle (rien : pas de bouton). */
  dismissLabel?: string
  /** Coach : la bulle a été fermée par l'utilisateur. */
  onDismiss?: () => void
}

/** Le mouvement, commun : l'entrée et la sortie de la sorte (--bubble-in / --bubble-out),
 *  depuis le côté de la cible (data-side), le glissé d'une cible à l'autre (data-glide). */
const MOTION =
  "group [--bubble-dx:0] [--bubble-dy:0] data-[side=top]:[--bubble-dy:1] data-[side=bottom]:[--bubble-dy:-1] " +
  "data-[side=left]:[--bubble-dx:1] data-[side=right]:[--bubble-dx:-1] animate-[bubble-in_var(--bubble-in)] " +
  "data-[state=closed]:pointer-events-none data-[state=closed]:animate-[bubble-out_var(--bubble-out)_forwards] " +
  "data-[glide]:transition-[left,top] data-[glide]:duration-fast data-[glide]:ease-smooth-out";

/** Une infobulle : à peine posée (4 px, 0,98, flou léger), vite partie. Une bulle qui
 *  demande d'être lue arrive en ressort, de plus loin. */
const TOOLTIP_MOTION =
  "[--bubble-distance:var(--distance-micro)] [--bubble-scale:var(--scale-small)] [--bubble-blur:var(--blur-small)] " +
  "[--bubble-in:var(--duration-quick)_ease-out] [--bubble-out:var(--duration-micro)_ease-out]";
const EMPHASIS_MOTION =
  "[--bubble-distance:var(--distance-base)] [--bubble-scale:var(--scale-large)] [--bubble-blur:var(--blur-medium)] " +
  "[--bubble-in:var(--duration-medium)_var(--ease-bounce)] [--bubble-out:var(--duration-quick)_var(--ease-smooth-out)]";

const BUBBLE_CLASS: Record<BubbleKind, string> = {
  tooltip:
    "pointer-events-none fixed left-0 top-0 z-[9999] w-max max-w-64 whitespace-pre-line rounded-md bg-surface_0 " +
    `px-2 py-1 text-xs font-medium leading-snug text-brand_black shadow-lg ${MOTION} ${TOOLTIP_MOTION}`,
  // Liseré dans la couleur d'accent du groupe (brand_main, thémée par groupe) : un état à signaler
  callout:
    "pointer-events-none fixed left-0 top-0 z-40 w-max max-w-56 whitespace-pre-line rounded-lg border " +
    "border-brand_main bg-surface_0 px-3 py-2 text-xs font-medium leading-snug text-brand_black shadow-lg " +
    `${MOTION} ${EMPHASIS_MOTION}`,
  coach:
    "fixed left-0 top-0 z-50 flex w-max max-w-64 flex-col items-start gap-2.5 whitespace-pre-line rounded-xl " +
    `bg-surface_0 px-3.5 py-3 text-sm font-medium leading-5 text-brand_black shadow-xl ${MOTION} ${EMPHASIS_MOTION}`,
};

/** La flèche glisse avec sa bulle. */
const ARROW_MOTION = "group-data-[glide]:transition-[left,top] group-data-[glide]:duration-fast " +
  "group-data-[glide]:ease-smooth-out";

const ARROW_CLASS: Record<BubbleKind, string> = {
  tooltip: `absolute size-2 rotate-45 bg-surface_0 ${ARROW_MOTION}`,
  callout: "absolute size-2.5 rotate-45 border-brand_main bg-surface_0",
  coach: "absolute size-2.5 rotate-45 bg-surface_0",
};

/** Le texte qui remplace celui d'une infobulle qui glisse se lève. */
const SWAP_CLASS = "animate-[bubble-swap_var(--duration-quick)_ease-out]";

// « Compris » à gauche, sous le texte (la bulle aligne ses enfants au début)
const DISMISS_CLASS =
  "rounded-md bg-brand_main px-2.5 py-1 text-xs font-semibold text-white transition-[background-color,transform] " +
  "duration-quick ease-smooth-out hover:bg-brand_darker active:scale-medium";

/** Les bulles bordées : le liseré de la flèche suit le bord de la bulle. */
const BORDERED: Record<BubbleKind, boolean> = { tooltip: false, callout: true, coach: false };

const OFFSET: Record<BubbleKind, number> = { tooltip: 6, callout: 10, coach: 10 };

/** Une sortie dont l'animation ne se termine pas (onglet en arrière-plan) retire quand même. */
const EXIT_FALLBACK = 400;

export class Bubble {
  readonly el: HTMLElement;
  private readonly arrowEl: HTMLElement;
  private body: HTMLElement;
  private stop: () => void;
  private destroyed = false;

  constructor(private anchor: BubbleAnchor, private readonly options: BubbleOptions) {
    this.el = document.createElement("div");
    this.el.className = BUBBLE_CLASS[options.kind];
    this.el.dataset.kind = options.kind;
    this.el.setAttribute("role", options.kind === "tooltip" ? "tooltip" : "status");

    this.body = this.buildBody();
    this.el.appendChild(this.body);

    if (options.kind === "coach" && options.dismissLabel) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = DISMISS_CLASS;
      button.textContent = options.dismissLabel;
      button.addEventListener("click", () => this.options.onDismiss?.());
      this.el.appendChild(button);
    }

    this.arrowEl = document.createElement("div");
    this.arrowEl.className = ARROW_CLASS[options.kind];
    this.el.appendChild(this.arrowEl);

    this.invertMode();
    document.body.appendChild(this.el);
    this.raise();
    this.stop = autoUpdate(anchor, this.el, () => this.position());
  }

  /** Une infobulle passe à une autre cible : elle y glisse, son texte se remplace. */
  retarget(anchor: BubbleAnchor, { text, content, placement }: Pick<BubbleOptions, "text" | "content" | "placement">) {
    if (this.destroyed) return;
    this.stop();
    this.anchor = anchor;
    Object.assign(this.options, { text, content, placement });
    const body = this.buildBody();
    body.className = SWAP_CLASS;
    this.body.replaceWith(body);
    this.body = body;
    this.invertMode();
    this.el.dataset.glide = "";
    this.stop = autoUpdate(anchor, this.el, () => this.position());
  }

  /** Repart vers sa cible, puis se retire (idempotent). */
  dismiss(): void {
    if (this.destroyed || this.el.dataset.state === "closed") return;
    this.el.dataset.state = "closed";
    this.el.addEventListener("animationend", (e) => { if (e.target === this.el) this.destroy(); });
    setTimeout(() => this.destroy(), EXIT_FALLBACK);
  }

  /** Retire la bulle sur-le-champ (idempotent). */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stop();
    this.el.remove();
  }

  private buildBody(): HTMLElement {
    const body = document.createElement("div");
    if (this.options.content) body.appendChild(this.options.content.cloneNode(true));
    else body.textContent = this.options.text ?? "";
    return body;
  }

  /** Ajoutée à <body>, la bulle sort de la surface qui porte le mode (racine de l'éditeur) :
   *  elle prend l'autre. Sur une surface sombre, elle reste sans mode, donc claire — celui
   *  de <html>, qui n'est jamais sombre aujourd'hui. */
  private invertMode(): void {
    const host = this.anchor instanceof Element ? this.anchor : this.anchor.contextElement;
    const dark = host?.closest("[data-mode]")?.getAttribute("data-mode") === "dark";
    if (dark) delete this.el.dataset.mode;
    else this.el.dataset.mode = "dark";
  }

  /** Dans la couche supérieure du navigateur (popover manuel) : une bulle ancrée dans un
   *  panneau `popover` ouvert (le color picker) passe au-dessus de lui, pas dessous. Les
   *  styles que l'agent utilisateur donne à un popover et que nos classes ne couvrent pas
   *  (inset, margin, overflow) sont remis à plat ; sans l'API, la bulle reste un div fixe. */
  private raise(): void {
    const el = this.el as HTMLElement & Partial<PopoverElement>;
    if (typeof el.showPopover !== "function") return;
    el.setAttribute("popover", "manual");
    Object.assign(el.style, { inset: "auto", margin: "0", overflow: "visible" });
    el.showPopover();
  }

  private async position(): Promise<void> {
    const { kind, placement = "top" } = this.options;
    const { x, y, placement: actual, middlewareData } = await computePosition(this.anchor, this.el, {
      strategy: "fixed",
      placement,
      middleware: [
        offset(OFFSET[kind]),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        arrow({ element: this.arrowEl }),
        hide(),
      ],
    });
    if (!this.el.isConnected) return;

    // Le côté de la cible où elle est posée : le sens de son entrée et de sa sortie
    const placed = actual.split("-")[0] as "top" | "bottom" | "left" | "right";
    this.el.dataset.side = placed;

    // Ancre hors de la vue (défilement) : la bulle se cache avec elle
    Object.assign(this.el.style, {
      left: `${x}px`,
      top: `${y}px`,
      visibility: middlewareData.hide?.referenceHidden ? "hidden" : "visible",
    });

    // La flèche, sur le bord qui fait face à l'ancre. Carré tourné de 45° : le liseré ne
    // court que sur ses deux côtés hors de la bulle, qui dépendent du bord.
    const side = { top: "bottom", bottom: "top", left: "right", right: "left" }[placed];
    const { x: arrowX, y: arrowY } = middlewareData.arrow || {};
    const style: Record<string, string> = {
      left: arrowX != null ? `${arrowX}px` : "",
      top: arrowY != null ? `${arrowY}px` : "",
      right: "",
      bottom: "",
      [side]: kind === "tooltip" ? "-4px" : "-6px",
    };
    if (BORDERED[kind]) {
      const outer = { bottom: ["Right", "Bottom"], top: ["Left", "Top"], left: ["Left", "Bottom"], right: ["Top", "Right"] }[side]!;
      Object.assign(style, { borderWidth: "0", borderStyle: "solid" });
      for (const edge of outer) style[`border${edge}Width`] = "1px";
    }
    Object.assign(this.arrowEl.style, style);

    // Elle grandit depuis sa flèche, la pointe vers la cible
    const half = this.arrowEl.offsetWidth / 2;
    const alongX = arrowX != null ? `${arrowX + half}px` : "50%";
    const alongY = arrowY != null ? `${arrowY + half}px` : "50%";
    this.el.style.transformOrigin = {
      top: `${alongX} 100%`, bottom: `${alongX} 0%`, left: `100% ${alongY}`, right: `0% ${alongY}`,
    }[placed];
  }
}

/** Le délai d'une infobulle : un passage de souris ne la fait pas clignoter. */
export const TOOLTIP_DELAY = 300;

/** Une infobulle quittée attend un instant la cible voisine avant de partir : elle y glisse. */
const GLIDE_GRACE = 100;
/** Juste après une infobulle, la suivante vient sans délai : on parcourt une barre d'outils. */
const WARM = 350;

/** L'infobulle à l'écran, une seule pour toutes les cibles. */
let shown: { bubble: Bubble; owner: HTMLElement } | null = null;
let leaving: ReturnType<typeof setTimeout> | null = null;
let warmUntil = 0;

function showTooltip(owner: HTMLElement, options: Pick<BubbleOptions, "text" | "content" | "placement">): void {
  if (leaving) clearTimeout(leaving);
  leaving = null;
  if (!shown) shown = { bubble: new Bubble(owner, { kind: "tooltip", ...options }), owner };
  else if (shown.owner !== owner) {
    shown.bubble.retarget(owner, options);
    shown.owner = owner;
  }
}

function hideTooltip(owner: HTMLElement, { now }: { now: boolean }): void {
  if (shown?.owner !== owner) return;
  if (leaving) clearTimeout(leaving);
  const close = () => {
    leaving = null;
    shown?.bubble.dismiss();
    shown = null;
    warmUntil = Date.now() + WARM;
  };
  if (now) close();
  else leaving = setTimeout(close, GLIDE_GRACE);
}

/**
 * Une infobulle sur un élément : au survol ou au focus (après TOOLTIP_DELAY, sans délai juste
 * après une autre), partie peu après qu'on sort — ou aussitôt qu'on clique ou qu'on appuie
 * sur Échap. D'une cible à la voisine, la même infobulle glisse. Rend la fonction qui la
 * détache.
 */
export function attachTooltip(
  el: HTMLElement,
  { text, content, placement = "top" }: { text?: string; content?: Node; placement?: Placement },
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const options = { text, content, placement };

  const show = () => {
    if (timer || (!text && !content)) return;
    if (shown || Date.now() < warmUntil) {
      showTooltip(el, options);
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      if (el.isConnected) showTooltip(el, options);
    }, TOOLTIP_DELAY);
  };
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  const leave = () => {
    cancel();
    hideTooltip(el, { now: false });
  };
  const hideNow = () => {
    cancel();
    hideTooltip(el, { now: true });
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") hideNow(); };

  const events: Array<[string, EventListener]> = [
    ["mouseenter", show], ["focusin", show],
    ["mouseleave", leave], ["focusout", leave], ["pointerdown", hideNow],
    ["keydown", onKey as EventListener],
  ];
  events.forEach(([type, fn]) => el.addEventListener(type, fn));

  return () => {
    events.forEach(([type, fn]) => el.removeEventListener(type, fn));
    hideNow();
  };
}

/**
 * Un panneau interactif (un color picker…) ouvert contre son déclencheur : un élément
 * `popover` natif, montré dans la couche supérieure du navigateur — ni rogné par un
 * conteneur qui défile, ni décalé par un ancêtre transformé — mais qui RESTE à sa place
 * dans le DOM : dans son formulaire, dans la portée de son contrôleur. Le navigateur le
 * ferme au clic dehors et sur Échap (popover="auto") ; ici on ne fait que le placer, avec
 * la même mécanique que les bulles. Pas de flèche : c'est un panneau, pas une bulle. Resté
 * dans le DOM, il hérite du mode (sombre) de sa surface, sans le recopier.
 *
 * Rend la fonction qui le ferme et cesse de le suivre ; il cesse aussi de le suivre de
 * lui-même quand le navigateur le ferme.
 */
/** L'API popover, que la lib DOM de ce TypeScript ne connaît pas encore. */
type PopoverElement = HTMLElement & { showPopover(): void; hidePopover(): void };

export function openPopover(
  anchor: Element,
  panel: PopoverElement,
  { placement = "bottom-start" }: { placement?: Placement } = {},
): () => void {
  const place = async () => {
    const { x, y, middlewareData } = await computePosition(anchor, panel, {
      strategy: "fixed",
      placement,
      middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 }), hide()],
    });
    Object.assign(panel.style, {
      position: "fixed", margin: "0", left: `${x}px`, top: `${y}px`,
      visibility: middlewareData.hide?.referenceHidden ? "hidden" : "visible",
    });
  };

  panel.showPopover();
  const stopFollowing = autoUpdate(anchor, panel, place);
  const onToggle = (e: Event) => {
    if ((e as Event & { newState?: string }).newState === "closed") cleanup();
  };
  const cleanup = () => {
    stopFollowing();
    panel.removeEventListener("toggle", onToggle);
  };
  panel.addEventListener("toggle", onToggle);

  return () => {
    cleanup();
    if (panel.matches(":popover-open")) panel.hidePopover();
  };
}
